import {
  AlertBox,
  Button,
  DatePicker,
  PhoneInput,
  RadioGroup,
  TextInput,
  UtilData,
} from "@life-line-lab/components-react";
import { Col, Row } from "antd";
import AddressInput from "Components/shared/address-input";
import IdentityNumberInput from "Components/shared/identity-number-input";
import { toaster } from "Helpers/toaster";
import useLLLForm, { LLLFormProvider } from "Hooks/form.hook";
import { isEmpty, map } from "lodash";
import { Profile, ProfileFormInput } from "Types/employee";
import moment from "moment";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { isErrorResponse } from "Utils/type-guard";
import { useProfileFormInitialValues } from "./hooks";
import { profileFormSchema } from "./profile-form.validation";

const statesCities = UtilData.StateCityList.country_MY;

type Props = {
  handleCloseStackScreen: () => void;
  handleSubmit?: (data: ProfileFormInput) => Promise<false | Profile>; // return response result
  data?: ProfileFormInput;
  isReadonly?: boolean;
};

const initialValues = {
  country: "MY",
  dob: "",
};

const ProfileForm = ({ handleCloseStackScreen, handleSubmit, data, isReadonly }: Props) => {
  const { t } = useTranslation();

  const getGenderOptions = () => [
    { value: "MALE", label: t("option.male") },
    { value: "FEMALE", label: t("option.female") },
  ];

  const { getInitialValues } = useProfileFormInitialValues();

  const formHookState = useLLLForm<Partial<ProfileFormInput>>({
    initialValues: getInitialValues(),
    yupSchema: profileFormSchema,
    normalizeOnValuesBeforeValidation: (values) => {
      return {
        ...values,
        dob: !values.dob ? "" : moment(values.dob).toISOString(),
        identificationIssueDate: !values.identificationIssueDate
          ? ""
          : moment(values.identificationIssueDate).toISOString(),
        identificationExpiryDate: !values.identificationExpiryDate
          ? ""
          : moment(values.identificationExpiryDate).toISOString(),
      };
    },
  });

  const {
    values,
    getCommonPropsByName,
    errors,
    formSubmitState,
    onCancelSubmit,
    onSubmit,
    setValues,
  } = formHookState;

  useEffect(() => {
    if (!isEmpty(data)) {
      setValues({
        ...initialValues,
        ...data,
        dob: data?.dob ? moment(data.dob) : undefined,
      });
    }
  }, [data]);

  const handleSelectChange = (val: any, name: string) =>
    setValues((prev) => ({
      ...prev,
      [name]: val,
    }));

  /**
   * ? just follows ?
   */
  // pass cityCode when city changes
  useEffect(() => {
    map(statesCities.STATES, (state) => {
      state.CITIES.map((city) => {
        if (values.city === city.NAME) {
          setValues((prev) => ({ ...prev, cityCode: city.CODE }));
        }
      });
    });
  }, [values.city]);

  // pass stateCode when state changes
  useEffect(() => {
    map(statesCities.STATES, (state) => {
      if (values.state === state.NAME) {
        setValues((prev) => ({ ...prev, stateCode: state.CODE }));
      }
    });
  }, [values.state]);

  const fieldIsReadonlyOnlyIfIsView = false || isReadonly;

  const handleFormSubmitResponse = async () => {
    if (!handleSubmit) return;

    const result = await handleSubmit(values as any);

    onCancelSubmit();

    if (!result || isErrorResponse(result)) {
      if (values._id) {
        toaster("error", t("toaster.updateEntityFailedTemplate", { entity: t("title.profile") }));
      } else {
        toaster("error", t("toaster.createEntityFailedTemplate", { entity: t("title.profile") }));
      }
      return;
    }

    if (values._id) {
      toaster(
        "success",
        t("toaster.updateEntitySuccessfulTemplate", { entity: t("title.profile") })
      );
    } else {
      toaster(
        "success",
        t("toaster.createEntitySuccessfulTemplate", { entity: t("title.profile") })
      );
    }

    handleCloseStackScreen && handleCloseStackScreen();
  };

  useEffect(() => {
    if (!formSubmitState.isSubmitting) return;

    handleFormSubmitResponse();
  }, [formSubmitState.isSubmitting]);

  return (
    <LLLFormProvider data={formHookState as any}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <Row gutter={16}>
          <Col xs={24} lg={24}>
            <TextInput
              isRequired
              isReadonly={fieldIsReadonlyOnlyIfIsView}
              {...getCommonPropsByName("fullName", ["error"])}
            />
            <AlertBox text={errors["fullName"]} />
          </Col>
          <Col xs={24}>
            <IdentityNumberInput
              isRequired
              isReadonly={fieldIsReadonlyOnlyIfIsView}
              input={values}
              error={errors}
              handleSelectChange={handleSelectChange}
            />
          </Col>
          <Col xs={24} lg={12}>
            {/* @ts-ignore */}
            <PhoneInput
              isRequired={true}
              isReadonly={fieldIsReadonlyOnlyIfIsView}
              {...getCommonPropsByName("contactNumber", ["error"])}
            />
            {errors["contactNumber.number"] || errors["contactNumber.dialCode"] ? (
              <AlertBox text={errors["contactNumber.number"]} />
            ) : (
              <AlertBox text={errors.contactNumber} />
            )}
          </Col>
          <Col xs={24} lg={12}>
            <TextInput
              isRequired={true}
              isReadonly={values._id || fieldIsReadonlyOnlyIfIsView}
              {...getCommonPropsByName("email", ["error"])}
            />
            <AlertBox text={errors["email"]} />
          </Col>
          <Col xs={24} lg={12}>
            <DatePicker
              format="YYYY-MM-DD"
              placeholder={t("label.dob")}
              isRequired
              isDisabled={fieldIsReadonlyOnlyIfIsView}
              {...getCommonPropsByName("dob", ["error"])}
            />
            <AlertBox text={errors["dob"]} />
          </Col>
          <Col xs={24} lg={12}>
            <RadioGroup
              direction="horizontal"
              isRequired={false}
              isReadonly={fieldIsReadonlyOnlyIfIsView}
              options={getGenderOptions()}
              {...getCommonPropsByName("gender", ["error"])}
            />
            <AlertBox text={errors["gender"]} />
          </Col>

          <Col xs={24}>
            <AddressInput
              error={errors}
              input={values}
              isRequired={true}
              isReadonly={fieldIsReadonlyOnlyIfIsView}
              handleSelectChange={handleSelectChange}
            />
          </Col>
        </Row>

        {!isReadonly && (
          <div className="form--actions">
            <Button
              className="mr-10"
              type="link"
              label={t("action.back")}
              size="large"
              onClick={handleCloseStackScreen}
            />

            <Button
              isLoading={formSubmitState.isSubmitting}
              type="primary"
              label={t("action.submit")}
              size="large"
              onClick={onSubmit}
            />
          </div>
        )}
      </form>
    </LLLFormProvider>
  );
};

export default ProfileForm;
